import { AdditionalJobData, JobCounters, JobExecutionStatus } from '@cdw-ae/common-data';

// Re-export for convenience
export {
  AdditionalJobData,
  ExecutionStatus,
  JobCounterKey,
  JobCounters,
  JobExecutionStatus,
  JobStepStatus,
  JobTypes,
  JOB_TYPE_DISPLAY_NAMES,
  JOB_TYPE_FIELD_POLICY,
  type JobType,
  type JobTypeFieldPolicy,
} from '@cdw-ae/common-data';

/**
 * Workflow names that support status tracking via Firestore
 * Used for collection group queries to filter executions by workflow type
 */
export const TRACKED_WORKFLOW_NAMES = ['full-asset-sync-workflow'] as const;

export type TrackedWorkflowName = (typeof TRACKED_WORKFLOW_NAMES)[number];

/**
 * Firestore collection name for job executions
 * Used in collection group queries across all customer documents
 */
export const FIRESTORE_EXECUTIONS_COLLECTION = 'executions';

/**
 * Maximum number of jobs to fetch and display per tab
 * Applied to Firestore queries, SQL queries, and final display
 * Note: Firestore results are filtered by workflow type, so actual count may be lower
 */
export const MAX_JOBS_PER_TAB = 15;

/**
 * Whitelist of workflow names to display in the job indicator
 * Only these workflows will be shown to users:
 * - device-assets-update-service-workflow: Bulk device updates and in-cell edits
 * - device-batch-get-workflow: User-initiated device refresh jobs
 * - export-workflow: Data export jobs (Sheets/CSV)
 * - audit-log-changes-export-workflow: Per-device before/after change export from an audit log entry (EDUS-2698)
 * - device-audit-history-export-workflow: Full change history export for one device (EDUS-2699)
 * - full-asset-sync-workflow: Full cache sync jobs (cache refresh)
 */
export const ALLOWED_JOB_WORKFLOWS = [
  'device-assets-update-service-workflow',
  'device-batch-get-workflow',
  'export-workflow',
  'audit-log-changes-export-workflow',
  'device-audit-history-export-workflow',
  'import-validate-workflow',
  'full-asset-sync-workflow',
] as const;

export type AllowedJobWorkflow = (typeof ALLOWED_JOB_WORKFLOWS)[number];

/**
 * Filter executions to only include allowed workflow types
 * Excludes background jobs like cache refresh workflows
 */
export function filterAllowedWorkflows(
  results: Array<{ workflowName?: string } & Record<string, unknown>>
): Array<{ workflowName?: string } & Record<string, unknown>> {
  return results.filter((doc) => {
    const workflowName = doc.workflowName as string;
    return ALLOWED_JOB_WORKFLOWS.includes(workflowName as AllowedJobWorkflow);
  });
}

/**
 * Firestore converter for strongly-typed JobExecutionStatus reads
 */
export function jobExecutionStatusConverter(
  data: Record<string, unknown>,
  id: string
): JobExecutionStatus {
  // Cast for typed property access (Firestore returns untyped document data)
  const doc = data as Partial<
    Omit<JobExecutionStatus, 'dt' | 'createdAt'> & {
      dt: unknown;
      createdAt: unknown;
    }
  >;

  // Handle Firestore Timestamp conversion for dt field
  const dtValue = doc.dt;
  const dt =
    dtValue && typeof dtValue === 'object' && 'toDate' in dtValue
      ? (dtValue as { toDate(): Date }).toDate()
      : new Date();

  // Handle Firestore Timestamp conversion for createdAt field
  const createdAtValue = doc.createdAt;
  const createdAt =
    createdAtValue &&
    typeof createdAtValue === 'object' &&
    'toDate' in createdAtValue
      ? (createdAtValue as { toDate(): Date }).toDate()
      : undefined;

  return {
    id: (doc.id as number) ?? 0,
    jobId: (doc.jobId as number) ?? 0,
    executionId: (doc.executionId as string) ?? id,
    executionStatus:
      doc.executionStatus as JobExecutionStatus['executionStatus'],
    customerId: (doc.customerId as number) ?? 0,
    workflowName: (doc.workflowName as string) ?? '',
    // `jobSteps` is the pre-EDUS-2845 field name, read off the raw document
    // because it is no longer part of the type. Workflow revisions that started
    // before the rename keep writing it for as long as their execution runs.
    jobStepStatuses: (doc.jobStepStatuses ??
      data['jobSteps']) as JobExecutionStatus['jobStepStatuses'],
    errorMessage: doc.errorMessage as string | undefined,
    dt,
    jobCounters: doc.jobCounters as JobCounters | undefined,
    additionalJobData: doc.additionalJobData as AdditionalJobData | undefined,
    correlationId: doc.correlationId as string | undefined,
    createdByUserId: typeof doc.createdByUserId === 'number' ? doc.createdByUserId : undefined,
    createdByUserEmail: doc.createdByUserEmail as string | undefined,
    createdAt,
  };
}
